// src/hooks/useUniversalToggleWithAddress.tsx
import { useState, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useVCardStore } from '../store/vcardStore';

/**
 * useUniversalToggleWithAddress
 * -------------------------------
 * This hook toggles the isActive state of a vCard.
 * When toggled:
 * - If active, it fetches the hotel address from the "luggage" vCard.
 * - If inactive, it fetches the home address from the "home" vCard.
 * It then writes the fetched address into the destination vCard's standard "address" field,
 * updating Firestore accordingly.
 */
const useUniversalToggleWithAddress = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const user = useAuthStore(state => state.user);
  const { toggleVCardWithAddress } = useVCardStore();

  const universalToggleWithAddress = useCallback(
    async (vcardIdOrCategory: string) => {
      if (!user) {
        setError('User not logged in');
        return;
      }
      setIsLoading(true);
      try {
        await toggleVCardWithAddress(vcardIdOrCategory, user.id);
      } catch (err: any) {
        setError(err.message || 'Error toggling vCard');
      } finally {
        setIsLoading(false);
      }
    },
    [user, toggleVCardWithAddress]
  );

  return { isLoading, error, universalToggleWithAddress };
};

export default useUniversalToggleWithAddress;
